import config from '../config';

/**
 * Formats a profile picture URL to ensure it's properly formatted
 * 
 * @param url The profile picture URL to format
 * @returns The formatted URL
 */
export const formatProfilePictureUrl = (url: string | undefined): string => {
  if (!url) return '';
  
  // If the URL is already absolute, return it as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // For development environment
  if (import.meta.env.DEV) {
    // For UAT environment
    if(import.meta.env.VITE_IS_UAT === 'true') {
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${config.API_URL}${path}`;
    }
    
    // For local development
    // Make sure URL starts with a slash for proper path resolution
    if (!url.startsWith('/')) {
      return `/${url}`;
    }
    return url;
  }
  
  // For production environment
  // Make sure the URL has the correct format when combined with API_URL
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${config.API_URL}${path}`;
};

/**
 * Formats an attachment URL to ensure it's properly formatted
 * This handles direct file paths for attachments
 * 
 * @param filePath The attachment file path
 * @returns The formatted URL
 */
export const formatAttachmentUrl = (filePath: string | undefined): string => {
  if (!filePath) return '';
  
  // If the URL is already absolute, return it as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // For development environment
  if (import.meta.env.DEV) {
    // For UAT environment
    const isUAT = import.meta.env.VITE_IS_UAT === 'true';
    if (isUAT) {
      const path = filePath.startsWith('/') ? filePath : `/${filePath}`;
      // Use the API_URL from config or fallback to the UAT server URL
      const baseUrl = config.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3000';
      return `${baseUrl}/uploads${path}`;
    }
    
    // For local development
    // Make sure URL starts with a slash for proper path resolution
    if (!filePath.startsWith('/')) {
      return `/uploads/${filePath}`;
    }
    return filePath.startsWith('/uploads') ? filePath : `/uploads/${filePath}`;
  }
  
  // For production environment
  // Make sure the URL has the correct format when combined with API_URL
  const path = filePath.startsWith('/') ? filePath : `/uploads/${filePath}`;
  return `${config.API_URL}${path}`;
};
